/**
 * ADR-0016 Phase 2 — Stock Snapshot projection from lots (+ ledger cursor).
 *
 * Truth remains InventoryMovement (Phase 5); snapshots accelerate reads and sync.
 * No import from stockMovementLedger (avoids circular dependency).
 */

import { isLotExpired } from '../../../utils/lotSystem';
import { DEFAULT_STORE_ID } from '../stores/storeCatalog';
import { readBestBusinessMergedPayload } from '../../../utils/sharedCore';

export const STOCK_SNAPSHOT_SCHEMA_VERSION = 1;

export function ensureStockSnapshotsArray(parsed) {
  if (!parsed || typeof parsed !== 'object') return [];
  if (!Array.isArray(parsed.stockSnapshots)) {
    parsed.stockSnapshots =
      parsed.stockSnapshots && typeof parsed.stockSnapshots === 'object'
        ? Object.values(parsed.stockSnapshots)
        : [];
  }
  return parsed.stockSnapshots;
}

export function stockSnapshotKey(storeId, productId, location = 'Principal') {
  return `${storeId || DEFAULT_STORE_ID}|${productId}|${location || 'Principal'}`;
}

function listLots(parsed) {
  if (Array.isArray(parsed?.lots)) return parsed.lots;
  if (parsed?.lots && typeof parsed.lots === 'object') return Object.values(parsed.lots);
  return [];
}

function resolveStoreIdLocal(parsed, entry = {}) {
  if (entry.storeId != null && String(entry.storeId).trim()) {
    return String(entry.storeId).trim();
  }
  if (parsed?.defaultStoreId != null && String(parsed.defaultStoreId).trim()) {
    return String(parsed.defaultStoreId).trim();
  }
  const stores = Array.isArray(parsed?.stores) ? parsed.stores : [];
  const first = stores.find((s) => s && (s.id != null || s.storeId != null));
  if (first) return String(first.id ?? first.storeId);
  return DEFAULT_STORE_ID;
}

function ensureInventoryLedgerMetaLocal(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return { sequencesByStore: {} };
  }
  if (!parsed.inventoryLedgerMeta || typeof parsed.inventoryLedgerMeta !== 'object') {
    parsed.inventoryLedgerMeta = { sequencesByStore: {} };
  }
  if (
    !parsed.inventoryLedgerMeta.sequencesByStore ||
    typeof parsed.inventoryLedgerMeta.sequencesByStore !== 'object'
  ) {
    parsed.inventoryLedgerMeta.sequencesByStore = {};
  }
  return parsed.inventoryLedgerMeta;
}

function isDamagedLot(lot) {
  const s = String(lot?.status || '').toLowerCase();
  return s === 'damaged' || s === 'write_off' || s === 'abgeschrieben';
}

function isInTransitLot(lot) {
  const s = String(lot?.status || '').toLowerCase();
  const loc = String(lot?.location || '').toLowerCase();
  return s === 'in_transit' || s === 'intransit' || loc === 'transit' || loc === 'in_transit';
}

export function computeStockBucketsFromLots(parsed, productId, location = 'Principal') {
  const locKey = String(location || 'Principal').trim().toLowerCase();
  let current = 0;
  let expired = 0;
  let damaged = 0;
  let inTransit = 0;
  let reserved = 0;
  let value = 0;

  for (const lot of listLots(parsed)) {
    if (!lot || String(lot.productId) !== String(productId)) continue;
    const lotLoc = String(lot.location || 'Principal').trim().toLowerCase();
    if (lotLoc !== locKey) continue;

    const qty = Number(lot.remainingQuantity) || 0;
    if (!(qty > 0)) continue;

    const unitCost = Number(lot.purchaseUnitPrice) || 0;
    reserved += Number(lot.reservedQuantity) || 0;

    if (isInTransitLot(lot)) {
      inTransit += qty;
      continue;
    }
    if (isDamagedLot(lot)) {
      damaged += qty;
      // Asset valuation = sellable only (aligned with lotsIndex / POS metrics)
      continue;
    }
    if (isLotExpired(lot)) {
      expired += qty;
      continue;
    }

    current += qty;
    value += qty * unitCost;
  }

  const available = Math.max(0, current - reserved);
  // averageCost on sellable asset only (not expired/damaged inventory)
  const averageCost = current > 0 ? value / current : null;

  return {
    current,
    reserved,
    available,
    damaged,
    expired,
    inTransit,
    value,
    averageCost: Number.isFinite(averageCost) ? averageCost : null
  };
}

export function ledgerSequenceForStore(parsed, storeId) {
  const meta = ensureInventoryLedgerMetaLocal(parsed);
  const key = storeId || DEFAULT_STORE_ID;
  const fromMeta = Number(meta.sequencesByStore?.[key]);
  if (Number.isFinite(fromMeta) && fromMeta > 0) return fromMeta;

  let max = 0;
  const lists = [
    Array.isArray(parsed?.stockMovements) ? parsed.stockMovements : [],
    Array.isArray(parsed?.stockMovementsArchive) ? parsed.stockMovementsArchive : []
  ];
  for (const list of lists) {
    for (const m of list) {
      if (String(m.storeId || DEFAULT_STORE_ID) !== String(key)) continue;
      const s = Number(m.ledgerSequence) || 0;
      if (s > max) max = s;
    }
  }
  return max;
}

export function upsertStockSnapshot(parsed, key, opts = {}) {
  if (!parsed || key?.productId == null) return null;
  const storeId = key.storeId || resolveStoreIdLocal(parsed, {});
  const location = key.location || 'Principal';
  const id = stockSnapshotKey(storeId, key.productId, location);
  const buckets = computeStockBucketsFromLots(parsed, key.productId, location);
  const ledgerSequence = ledgerSequenceForStore(parsed, storeId);
  const list = ensureStockSnapshotsArray(parsed);
  const idx = list.findIndex((s) => s && s.id === id);
  const prev = idx >= 0 ? list[idx] : null;
  const prevVersion = Number(prev?.version) || 0;
  const changed =
    !prev ||
    prev.current !== buckets.current ||
    prev.reserved !== buckets.reserved ||
    prev.available !== buckets.available ||
    prev.damaged !== buckets.damaged ||
    prev.expired !== buckets.expired ||
    prev.inTransit !== buckets.inTransit ||
    Number(prev.value) !== Number(buckets.value) ||
    Number(prev.ledgerSequence) !== Number(ledgerSequence);

  const version =
    opts.forceVersionBump || changed ? prevVersion + 1 : prevVersion || 1;

  const row = {
    id,
    schemaVersion: STOCK_SNAPSHOT_SCHEMA_VERSION,
    storeId,
    productId: Number.isFinite(Number(key.productId))
      ? Number(key.productId)
      : key.productId,
    location,
    locationId: String(location),
    current: buckets.current,
    reserved: buckets.reserved,
    available: buckets.available,
    damaged: buckets.damaged,
    expired: buckets.expired,
    inTransit: buckets.inTransit,
    value: buckets.value,
    averageCost: buckets.averageCost,
    version,
    ledgerSequence,
    createdAt: prev?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  if (idx >= 0) list[idx] = row;
  else list.push(row);
  return row;
}

export function applyStockSnapshotsAfterMovements(parsed, movements = []) {
  if (!parsed) return [];
  const keys = new Map();
  for (const m of movements || []) {
    if (m?.productId == null) continue;
    const storeId = m.storeId || resolveStoreIdLocal(parsed, m);
    const location = m.location || 'Principal';
    const id = stockSnapshotKey(storeId, m.productId, location);
    keys.set(id, { storeId, productId: m.productId, location });
    if (m.toLocation && String(m.movementType || '').includes('TRANSFER')) {
      const id2 = stockSnapshotKey(storeId, m.productId, m.toLocation);
      keys.set(id2, {
        storeId,
        productId: m.productId,
        location: m.toLocation
      });
    }
  }
  const updated = [];
  for (const key of keys.values()) {
    const row = upsertStockSnapshot(parsed, key, { forceVersionBump: true });
    if (row) updated.push(row);
  }
  return updated;
}

export function rebuildStockSnapshotsFromLots(parsed) {
  if (!parsed) return [];
  const storeId = resolveStoreIdLocal(parsed, {});
  const seen = new Map();
  for (const lot of listLots(parsed)) {
    if (lot?.productId == null) continue;
    const location = lot.location || 'Principal';
    const id = stockSnapshotKey(storeId, lot.productId, location);
    if (!seen.has(id)) {
      seen.set(id, { storeId, productId: lot.productId, location });
    }
  }
  parsed.stockSnapshots = [];
  const out = [];
  for (const key of seen.values()) {
    const row = upsertStockSnapshot(parsed, key, { forceVersionBump: true });
    if (row) out.push(row);
  }
  return out;
}

export function listStockSnapshots(parsed, filters = {}) {
  const list = ensureStockSnapshotsArray(parsed);
  return list.filter((s) => {
    if (!s) return false;
    if (filters.productId != null && String(s.productId) !== String(filters.productId)) {
      return false;
    }
    if (filters.location && s.location !== filters.location) return false;
    if (filters.storeId && String(s.storeId) !== String(filters.storeId)) return false;
    return true;
  });
}

export function getSnapshotCurrent(parsed, productId, location = 'Principal', storeId = null) {
  const sid = storeId || resolveStoreIdLocal(parsed, {});
  const id = stockSnapshotKey(sid, productId, location);
  const list = ensureStockSnapshotsArray(parsed);
  const hit = list.find((s) => s && s.id === id);
  if (hit) return Number(hit.current) || 0;
  return computeStockBucketsFromLots(parsed, productId, location).current;
}

/**
 * Read stockSnapshots from merged business payload (sync, blob-first).
 * Used by Inventaire KPIs when context does not expose snapshots (ADR-0016 Phase 3).
 * @param {string|null|undefined} businessId
 * @returns {object[]}
 */
export function readStockSnapshotsSyncForBusiness(businessId) {
  if (!businessId) return [];
  try {
    const parsed = readBestBusinessMergedPayload(businessId)?.parsed;
    if (Array.isArray(parsed?.stockSnapshots)) return parsed.stockSnapshots;
    if (parsed?.stockSnapshots && typeof parsed.stockSnapshots === 'object') {
      return Object.values(parsed.stockSnapshots);
    }
  } catch {
    /* storage not ready */
  }
  return [];
}

/**
 * Sum inventory asset value from snapshot projection (ADR-0016 Phase 3 reports).
 * @param {object[]} stockSnapshots
 * @param {{ productIds?: Set<string>|string[], storeId?: string }} [filters]
 * @returns {{
 *   totalValue: number,
 *   totalCurrent: number,
 *   totalAvailable: number,
 *   totalReserved: number,
 *   totalExpired: number,
 *   totalDamaged: number,
 *   totalInTransit: number,
 *   maxLedgerSequence: number,
 *   source: string,
 *   snapshotCount: number
 * }}
 */
export function sumStockSnapshotInventoryValue(stockSnapshots = [], filters = {}) {
  const list = Array.isArray(stockSnapshots) ? stockSnapshots : Object.values(stockSnapshots || {});
  let catalogIds = null;
  if (filters.productIds instanceof Set) catalogIds = filters.productIds;
  else if (Array.isArray(filters.productIds)) catalogIds = new Set(filters.productIds.map(String));

  let totalValue = 0;
  let totalCurrent = 0;
  let totalAvailable = 0;
  let totalReserved = 0;
  let totalExpired = 0;
  let totalDamaged = 0;
  let totalInTransit = 0;
  let maxLedgerSequence = 0;
  let snapshotCount = 0;

  for (const s of list) {
    if (!s) continue;
    if (catalogIds && !catalogIds.has(String(s.productId))) continue;
    if (filters.storeId != null && String(s.storeId) !== String(filters.storeId)) continue;
    if (filters.location && s.location !== filters.location) continue;

    snapshotCount += 1;
    totalValue += Number(s.value) || 0;
    totalCurrent += Number(s.current) || 0;
    totalAvailable += Number(s.available) || 0;
    totalReserved += Number(s.reserved) || 0;
    totalExpired += Number(s.expired) || 0;
    totalDamaged += Number(s.damaged) || 0;
    totalInTransit += Number(s.inTransit) || 0;
    const seq = Number(s.ledgerSequence) || 0;
    if (seq > maxLedgerSequence) maxLedgerSequence = seq;
  }

  return {
    totalValue,
    totalCurrent,
    totalAvailable,
    totalReserved,
    totalExpired,
    totalDamaged,
    totalInTransit,
    maxLedgerSequence,
    source: 'stock_snapshots',
    snapshotCount
  };
}
